import { Card } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import ObjectCardInfoItem from '../ObjectCardInfo/ObjectCardInfoItem';
import SvgDollar from 'components/svg/SvgDollar';
import SvgPackage from 'components/svg/SvgPackage';
import SvgUsers from 'components/svg/SvgUsers';
import { formatPrice } from 'utils/helpers/functions/formatPrice';
import {
    getBaseCurrencyCode,
    getBaseCurrencyPrefix,
    getCurrencyCode,
    withBaseCurrency,
    withLocalCurrency,
} from 'utils/helpers/currency/currencyHelpers';
import { useObjectCard } from '../context/ObjectCardProvider';
import vconst from 'utils/helpers/structs/vconst';
import { useMemo, useState } from 'react';
import { useSettings } from 'context/SettingsContext';
import useSettingsParams from 'hooks/useSettingsParams';
import { useToast } from 'context/ToastContext';
import { useAuth } from 'context/AuthContext';
import * as apiObject from 'api/apiObjects';
import ObjectPayStatus from '../../ObjectsList/ObjectPayStatus/ObjectPayStatus';
import {
    getObjectPaymentsCoverageRatio,
    isObjectPaymentsCovered,
} from '../../ObjectsList/helpers/objectPayCoverage';
import { hasUserAccess, canViewSales } from 'utils/helpers/access/hasUserAccess';
import OffHistoryPrice from '../OffHistoryPrice';
import SolarIcon from 'components/icons/SolarIcon';

type SaleCostKind = 'materials' | 'services';

const SaleCardInfo = ({ costKind = 'materials' }: { costKind?: SaleCostKind }) => {
    const { t } = useTranslation(['Objects', 'common']);
    const { showToast } = useToast();
    const { auth } = useAuth();
    const { objectId, vObject, vObjectInfo, setVObject, setShowSpinner, isEditLocked } = useObjectCard(); 
    const { localSettings } = useSettings();
    const params = useSettingsParams();
    const showPayments = Boolean(params.objectPaymentControl);
    const canManualPayStatus = Boolean(params.objectPaymentControl && params.manualPayStatus);
    const [payStatusBusy, setPayStatusBusy] = useState(false);
    const [showHistoryPrice, setShowHistoryPrice] = useState(false);
    const canChangePrice =
        (canViewSales(auth?.user) || hasUserAccess(auth?.user, 'objectCreate')) && !isEditLocked;
    const rate = Number(localSettings?.RATE?.current ?? 1) || 1;
    const isUahMode = rate === 1;
    const object = vObject?.object;
    const priceObject = Number(object?.objectPrice || 0);
    const priceObjectRate = Number(object?.objectPriceRate || 0);
    const isServicesCost = costKind === 'services';
    let priceCost = Number(vObjectInfo?.prodPrice || 0);
    let priceCostRate = Number(vObjectInfo?.prodPriceRate || 0);
    if (isServicesCost) {
        priceCost = 0;
        priceCostRate = 0;
        const expendTeams = vObject?.expendTeams;
        expendTeams?.forEach((expend: any) => {
            if (expend.income_status == vconst.incomeStatActive) {
                priceCost += Number(expend.income_amount || 0);
                priceCostRate += Number(expend.income_amount_rate || 0);
            }
        });
    }
    const margin = priceObject - priceCost;
    const marginRate = priceObjectRate - priceCostRate;

    const paidUsd = Number(object?.paidUsd || 0);
    const paidUah = Number(object?.paidUah || 0);
    const hasPayments = paidUsd !== 0 || paidUah !== 0;
    const coverageParams = {
        objectPrice: priceObject,
        objectPriceRate: priceObjectRate,
        paidUsd,
        paidUah,
        rate,
    };
    const paymentsCovered = isObjectPaymentsCovered(coverageParams);
    const coverageRatio = getObjectPaymentsCoverageRatio(coverageParams);
    const coveragePercent = Math.min(100, Math.round(Math.max(0, coverageRatio) * 100));
    const paymentsTone = !hasPayments ? 'empty' : paymentsCovered ? 'covered' : 'partial';
    const paymentsStatusLabel = paymentsCovered
        ? t('infoItems.objectPaymentsCovered')
        : t('infoItems.objectPaymentsPartial');
    const paymentsStatusIcon = paymentsCovered ? 'bi-check-circle-fill' : 'bi-hourglass-split';

    const profitability = useMemo(() => margin, [margin]);

    const handlePayStatusChange = async (payStatus: number) => {
        if (!objectId || payStatusBusy) return;
        setPayStatusBusy(true);
        setShowSpinner?.(true);
        try {
            const res = await apiObject.setPayStatus(objectId, payStatus);
            if (res) {
                setVObject((prev: any) => {
                    if (!prev?.object) return prev;
                    return {
                        ...prev,
                        object: {
                            ...prev.object,
                            objectPayStatus: payStatus,
                        },
                    };
                });
                showToast(t('messPayStatusChanged'), 'success');
            } else {
                showToast(t('common:messError'), 'danger');
            }
        } finally {
            setPayStatusBusy(false);
            setShowSpinner?.(false);
        }
    };

    const handlePriceChanged = async (nextPrice: number, nextRate: number) => {
        setVObject((prev: any) => {
            if (!prev?.object) return prev;
            return {
                ...prev,
                object: {
                    ...prev.object,
                    objectPrice: nextPrice,
                    objectPriceRate: nextRate,
                },
            };
        });
    };

    return (
        <>
            <OffHistoryPrice
                show={showHistoryPrice}
                setShow={setShowHistoryPrice}
                objectId={Number(objectId)}
                objectPrice={priceObject}
                objectPriceRate={priceObjectRate}
                onSuccess={handlePriceChanged}
            />
            <Card className="object-analytics-card">
                <Card.Body className="object-analytics-card__body p-0">
                    <div className="object-analytics-card__header">
                        <h5 className="object-analytics-card__title">
                            <SolarIcon name="graph-up-arrow" className="object-analytics-card__title-icon" aria-hidden="true" />
                            {t(isServicesCost ? 'titleServiceSaleAnalytics' : 'titleSaleAnalytics')}
                        </h5>
                    </div>

                    <div className="object-analytics-card__content">
                        <ul className="object-analytics-list list-unstyled mb-0">
                            <ObjectCardInfoItem
                                isUahMode={isUahMode}
                                title={t(isServicesCost ? 'infoItems.saleAmount' : 'infoItems.saleAmount')}
                                icon={<SvgDollar width={18} />}
                                amount={priceObject}
                                amountTitle={undefined}
                                secondAmount={undefined}
                                secondAmountTitle={undefined}
                                actionLabel={canChangePrice ? t('common:buttons.change') : undefined}
                                onAction={canChangePrice ? () => setShowHistoryPrice(true) : undefined}
                                noteLabel={undefined}
                                noteAmount={undefined}
                            />

                            <ObjectCardInfoItem
                                isUahMode={isUahMode}
                                title={t(isServicesCost ? 'infoItems.saleServicesCost' : 'infoItems.saleMaterialsCost')}
                                icon={isServicesCost ? <SvgUsers width={18} /> : <SvgPackage width={18} />}
                                amount={-priceCost}
                                amountTitle={priceCostRate}
                                secondAmount={undefined}
                                secondAmountTitle={undefined}
                                actionLabel={undefined}
                                onAction={undefined}
                                noteLabel={undefined}
                                noteAmount={undefined}
                            />

                            <ObjectCardInfoItem
                                isUahMode={isUahMode}
                                title={t('infoItems.saleMargin')}
                                icon={<SvgDollar width={18} />}
                                amount={margin}
                                amountTitle={marginRate}
                                secondAmount={undefined}
                                secondAmountTitle={undefined}
                                actionLabel={undefined}
                                onAction={undefined}
                                noteLabel={undefined}
                                noteAmount={undefined}
                            />
                        </ul>
                    </div>

                    {showPayments ? (
                        <div className={`object-analytics-payments object-analytics-payments--${paymentsTone}`}>
                            <div className="object-analytics-payments__glow" aria-hidden="true" />
                            <div className="object-analytics-payments__head">
                                <span className="object-analytics-payments__icon" aria-hidden="true">
                                    <SolarIcon name="wallet2" />
                                </span>
                                <div className="object-analytics-payments__titles">
                                    <div className="object-analytics-payments__title-row">
                                        <div className="object-analytics-payments__title-stack">
                                            <span className="object-analytics-payments__label">
                                                {t('infoItems.objectPayments')}
                                            </span>
                                            <span className="object-analytics-payments__hint">
                                                {t('infoItems.objectPaymentsHint')}
                                            </span>
                                        </div>
                                        {hasPayments ? (
                                            <span className="object-analytics-payments__status">
                                                <SolarIcon name={paymentsStatusIcon} aria-hidden="true" />
                                                {paymentsStatusLabel}
                                            </span>
                                        ) : null}
                                    </div>
                                </div>
                            </div>

                            <div className="object-analytics-payments__chips">
                                {hasPayments ? (
                                    <>
                                        {paidUsd !== 0 ? (
                                            <div className="object-analytics-payments__chip">
                                                <span className="object-analytics-payments__chip-cur">{getBaseCurrencyCode()}</span>
                                                <span className="object-analytics-payments__chip-val">
                                                    {withBaseCurrency(formatPrice(paidUsd))}
                                                </span>
                                            </div>
                                        ) : null}
                                        {paidUah !== 0 ? (
                                            <div className="object-analytics-payments__chip">
                                                <span className="object-analytics-payments__chip-cur">{getCurrencyCode()}</span>
                                                <span className="object-analytics-payments__chip-val">
                                                    {withLocalCurrency(formatPrice(paidUah))}
                                                </span>
                                            </div>
                                        ) : null}
                                    </>
                                ) : (
                                    <div className="object-analytics-payments__chip object-analytics-payments__chip--empty">
                                        <span className="object-analytics-payments__chip-val">—</span>
                                    </div>
                                )}
                            </div>

                            <div className="object-analytics-payments__progress">
                                <div className="object-analytics-payments__progress-meta">
                                    <span>{t('infoItems.objectPaymentsCoverage')}</span>
                                    <span className="object-analytics-payments__progress-pct">
                                        {coveragePercent}%
                                    </span>
                                </div>
                                <div
                                    className="object-analytics-payments__track"
                                    role="progressbar"
                                    aria-valuenow={coveragePercent}
                                    aria-valuemin={0}
                                    aria-valuemax={100}
                                >
                                    <div
                                        className="object-analytics-payments__fill"
                                        style={{ width: `${coveragePercent}%` }}
                                    />
                                </div>
                            </div>
                        </div>
                    ) : null}

                    {canManualPayStatus ? (
                        <div className="object-analytics-pay-status">
                            <span className="object-analytics-pay-status__label">
                                {t('fields.payStatus')}
                            </span>
                            <ObjectPayStatus
                                objectId={Number(objectId)}
                                payStatus={object?.objectPayStatus}
                                objectPrice={priceObject}
                                objectPriceRate={priceObjectRate}
                                paidUsd={paidUsd}
                                paidUah={paidUah}
                                rate={rate}
                                editable
                                showCoveredHint={false}
                                disabled={payStatusBusy}
                                onChange={handlePayStatusChange}
                            />
                        </div>
                    ) : null}

                    <div className="object-analytics-card__footer">
                        <span className="object-analytics-card__footer-label">
                            {t('infoItems.saleProfitability')}
                        </span>
                        <h4 className={`object-analytics-card__footer-value mb-0${profitability < 0 ? ' text-danger' : ''}`}>
                            {isUahMode ? (
                                <>{withLocalCurrency(formatPrice(profitability))}</>
                            ) : (
                                <>
                                    <small className={profitability < 0 ? 'text-danger' : 'text-muted'}>{getBaseCurrencyPrefix()}</small>
                                    {formatPrice(profitability)}
                                </>
                            )}
                        </h4>
                    </div>
                </Card.Body>
            </Card>
        </>
    );
};

export default SaleCardInfo;
